package dev.lingvo.site.pages.app.projects.slug

import dev.lingvo.shared.BulkKeysRequest
import dev.lingvo.shared.BulkKeysResponse
import dev.lingvo.shared.FileDto
import dev.lingvo.shared.CreateKeyRequest
import dev.lingvo.shared.KeyCoverageDto
import dev.lingvo.shared.KeyListResponse
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.TranslationState
import dev.lingvo.shared.UpdateKeyRequest
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.ProjectActionKey
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource

/**
 * Matches the server's own ceiling (`KeyRepo.MAX_COVERAGE_KEYS`, `limit` on `GET /keys`), so a page
 * never asks for more than the coverage endpoint will answer.
 */
private const val KEY_PAGE_SIZE = 200

/** What state a target locale is in, plus the "any state" default that sends no filter at all. */
internal enum class KeyStateFilter(val apiState: TranslationState?) {
    // The editor's filter over the same three server states says "All" and "Untranslated"
    // (`EditorModels.kt`), and every counter in the product says "Untranslated" too. One state, one
    // word.
    ANY(null),
    UNTRANSLATED(TranslationState.EMPTY),
    TRANSLATED(TranslationState.TRANSLATED),
    APPROVED(TranslationState.APPROVED),
    ;

    /**
     * The word on the picker.
     *
     * **A function, not a constructor argument.** As a `val` these four were evaluated at enum
     * class-init — before any catalogue has been fetched — so four of the product's core state
     * words were frozen in English for the life of the page, in the one control that says which
     * strings a translator is looking at.
     */
    fun label(): String = when (this) {
        ANY -> tr("app.projects.slug.project_keys_data.state_all", "All")
        UNTRANSLATED -> tr("app.projects.slug.project_keys_data.state_untranslated", "Untranslated")
        TRANSLATED -> tr("app.projects.slug.project_keys_data.state_translated", "Translated")
        APPROVED -> tr("app.projects.slug.project_keys_data.state_approved", "Approved")
    }
}

/** The project and its files — loaded once per visit, independent of the filters below. */
internal data class KeysFoundationData(val project: ProjectDetailDto, val files: List<FileDto>) {
    val targetLocales: List<String> get() = project.locales.filter { it != project.sourceLocale }
}

/** Everything the current filters produced: one page of rows, and the coverage for just those rows. */
internal data class KeysSlice(
    val rows: List<KeyRow>,
    /** Key id → locale → state, for every row in [rows] and every target locale. */
    val coverage: Map<String, Map<String, TranslationState>>,
    /** How many keys match the filters, independent of how many of them [rows] holds. */
    val total: Int,
    /** Pass to the next request's `after` to continue past [rows], or null when there is no more. */
    val nextCursor: String?,
)

internal data class KeysQuery(
    val search: String,
    val fileId: String,
    val locale: String,
    val state: TranslationState?,
    val tag: String,
)

internal data class KeysListRequest(
    val projectSlug: String,
    val locale: String?,
    val fileId: String?,
    val search: String?,
    val state: TranslationState?,
    val tag: String?,
    val limit: Int,
    val after: String?,
)

internal data class KeysReadTransport(
    val listFiles: suspend (String) -> List<FileDto>,
    val listKeys: suspend (KeysListRequest) -> KeyListResponse,
    val keyCoverage: suspend (String, List<String>, List<String>) -> List<KeyCoverageDto>,
)

private val apiKeysReadTransport = KeysReadTransport(
    listFiles = ApiClient::listFiles,
    listKeys = { request ->
        ApiClient.listKeys(
            projectSlug = request.projectSlug,
            locale = request.locale,
            fileId = request.fileId,
            search = request.search,
            state = request.state,
            tag = request.tag,
            limit = request.limit,
            after = request.after,
        )
    },
    keyCoverage = ApiClient::keyCoverage,
)

internal data class KeysMutationTransport(
    val createKey: suspend (String, CreateKeyRequest) -> Unit,
    val updateKey: suspend (String, String, UpdateKeyRequest) -> Unit,
    val deleteKey: suspend (String, String) -> Unit,
    /**
     * Every selection-wide change, in one request and one transaction.
     *
     * The loop this replaces issued one `PATCH` per selected row and reported a partial result:
     * tagging four hundred keys was four hundred round trips, and a failure halfway through left
     * the table showing rows the server had already changed. `POST /keys/bulk` answers with what it
     * actually did — [dev.lingvo.shared.BulkKeysResponse.applied] counts rows the database changed,
     * not rows the caller sent.
     *
     * Defaulted so a fixture that exercises single-key mutations does not have to name a seam it
     * never reaches; a test that presses a bulk control replaces it explicitly.
     */
    val bulk: suspend (String, BulkKeysRequest) -> BulkKeysResponse = ApiClient::keysBulk,
)

internal val apiKeysMutationTransport = KeysMutationTransport(
    createKey = { slug, request -> ApiClient.createKey(slug, request).let {} },
    updateKey = { slug, keyId, request -> ApiClient.updateKey(slug, keyId, request).let {} },
    deleteKey = ApiClient::deleteKey,
    bulk = ApiClient::keysBulk,
)

internal suspend fun loadKeysFoundation(
    key: ProjectActionKey,
    expectedSlug: String,
    memberships: List<WorkspaceDto>,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(expectedSlug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    transport: KeysReadTransport = apiKeysReadTransport,
): KeysFoundationData {
    return requestProjectResource(
        key = key,
        expectedSlug = expectedSlug,
        memberships = memberships,
        currentRoute = currentRoute,
        currentMemberships = currentMemberships,
    ) { route ->
        KeysFoundationData(
            project = route.project,
            files = transport.listFiles(route.project.slug),
        )
    }
}

internal suspend fun loadKeysSlice(
    key: ProjectActionKey,
    expectedSlug: String,
    targetLocales: List<String>,
    query: KeysQuery,
    after: String?,
    memberships: List<WorkspaceDto>,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(expectedSlug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    transport: KeysReadTransport = apiKeysReadTransport,
): KeysSlice {
    val page = requestProjectResource(
        key = key,
        expectedSlug = expectedSlug,
        memberships = memberships,
        currentRoute = currentRoute,
        currentMemberships = currentMemberships,
    ) { route ->
        transport.listKeys(
            KeysListRequest(
                projectSlug = route.project.slug,
                locale = query.locale.ifBlank { null },
                fileId = query.fileId.ifBlank { null },
                search = query.search.ifBlank { null },
                state = query.state,
                tag = query.tag.ifBlank { null },
                limit = KEY_PAGE_SIZE,
                after = after,
            ),
        )
    }
    val coverage = requestProjectResource(
        key = key,
        expectedSlug = expectedSlug,
        memberships = memberships,
        currentRoute = currentRoute,
        currentMemberships = currentMemberships,
    ) { route ->
        transport.keyCoverage(route.project.slug, page.items.map { it.key.id }, targetLocales)
    }
        .associate { it.keyId to it.states }
    return KeysSlice(page.items, coverage, page.total, page.nextCursor)
}
